import React from 'react';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { companyInfo } from '../data/navigation';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './Showroom.css';

/**
 * Showroom page showcasing the Mims Family Painting studio and materials
 * Features a hero banner, description section, features grid, and visit CTA
 */
export const Showroom: React.FC = () => {
  useDocumentTitle('Showroom');
  const descriptionRef = useScrollReveal({ threshold: 0.15 });
  const featuresRef = useScrollReveal({ threshold: 0.1 });
  const ctaRef = useScrollReveal({ threshold: 0.15 });

  const getImage = (path: string) => {
    try {
      return require(`../assets/images/${path}`);
    } catch {
      return '';
    }
  };

  const features = [
    {
      number: '01',
      title: 'Material Library',
      description:
        'A working collection of paints, stains, wallcoverings, and specialty finishes to review in person',
    },
    {
      number: '02',
      title: 'Sample Boards',
      description:
        'Finish samples that help compare color, sheen, and texture in the context of your project',
    },
    {
      number: '03',
      title: 'Design Consultation',
      description:
        'A focused conversation about materials, surface conditions, and the work ahead',
    },
  ];

  return (
    <div className="showroom-page page-enter">
      {/* Hero Section */}
      <section className="showroom-hero">
        <div
          className="showroom-hero__image"
          style={{
            backgroundImage: `url(${getImage('showroom/studio2.jpg')})`,
          }}
        >
          <div className="showroom-hero__overlay" />
        </div>
        <div className="showroom-hero__content">
          <h1 className="showroom-hero__title">The Showroom</h1>
        </div>
      </section>

      {/* Description + Features combined — two-column on desktop */}
      <section className="showroom-description">
        <div className="showroom-container">
          <div ref={descriptionRef} className="showroom-description__content">
            <div className="showroom-description__grid">
              {/* Left: Text */}
              <div className="showroom-description__left">
                <SectionHeading
                  eyebrow="Visit Us"
                  heading="See materials in the right light"
                  align="left"
                />

                <div className="showroom-description__text">
                  <p>
                    The Mims Family Painting showroom is a place to look closely at finishes,
                    textures, and color before they are specified for a project. Homeowners,
                    architects, designers, and contractors can review options with our team and
                    talk through how they will work in the space.
                  </p>

                  <p>
                    Alongside material libraries, the studio includes finished examples of specialty
                    techniques, including glazes, metallic finishes, stains, and repair work. It is a
                    useful setting for making decisions before the work moves to site.
                  </p>

                  <p>
                    Visits are by appointment so there is time to focus on your project.
                  </p>
                </div>

                <div className="showroom-description__cta">
                  <Button asLink="/contact" variant="primary" size="lg">
                    Arrange a Visit
                  </Button>
                </div>
              </div>

              {/* Right: Image */}
              <div className="showroom-description__image-col">
                <img
                  src={getImage('showroom/studio2.jpg')}
                  alt="Mims Family Painting showroom with finish samples"
                  className="showroom-description__image"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="showroom-features">
        <div className="showroom-container">
          <div ref={featuresRef} className="showroom-features__grid">
            {features.map((feature) => (
              <div key={feature.number} className="feature-card">
                <div className="feature-card__number">{feature.number}</div>
                <h3 className="feature-card__title">{feature.title}</h3>
                <p className="feature-card__description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visit CTA Section */}
      <section className="showroom-visit">
        <div className="showroom-container">
          <div ref={ctaRef} className="showroom-visit__content">
            <h2 className="showroom-visit__heading">Plan a Visit</h2>

            <div className="showroom-visit__info">
              <div className="showroom-visit__detail">
                <p className="showroom-visit__label">Phone</p>
                <a href={`tel:${companyInfo.phone}`} className="showroom-visit__value">
                  {companyInfo.phone}
                </a>
              </div>

              <div className="showroom-visit__detail">
                <p className="showroom-visit__label">Email</p>
                <a href={`mailto:${companyInfo.email}`} className="showroom-visit__value">
                  {companyInfo.email}
                </a>
              </div>

              <div className="showroom-visit__detail">
                <p className="showroom-visit__label">Service Area</p>
                <p className="showroom-visit__value">{companyInfo.address}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

Showroom.displayName = 'Showroom';
